import {
  Dimensions,
  ProductType,
  ProductInfoComponent,
  CompartmentComponent,
  TopPanelComponent
} from '@inter-ikea-storageone/range';
import {
  ComponentIds,
  Entity,
  HierarchyUtils,
  ProductUtils,
  Size,
  TemplateDataComponent,
  TransformUtils,
  Vector
} from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { AssemblyType } from './GenerateAssembly';
import { rangeIsBesta } from 'utils/queryUtils';

type CatalogMeta = {
  design?: string;
  image: string;
  ageGroup?: string;
  defaultFrontLayout?: string;
  applicableFrameTypes?: string[];
  imagePrefix?: string;
};

export type CatalogData = {
  id: string;
  type: string;
  dexf: string;
  modelURI: string;
  autoAdaptGroup?: string;
  template: {
    id: string;
    size: Dimensions;
    comSize: Dimensions;
    parts: TemplatePart[];
  };
  meta: CatalogMeta;
};

type EntityData = {
  id: string;
  partKey: string;
  position: {
    x: number;
    y: number;
    z: number;
    rx: number;
    ry: number;
    rz: number;
  };
  children: EntityData[];
};

type EntitySize = {
  size: Vector;
  comSize: Size;
};

type TemplatePart = {
  id: string;
  partKey: string;
  main: boolean;
};

const getAssemblyTemplateId = (
  id: string,
  width: number,
  depth: number,
  height: number,
  assemblyType: AssemblyType,
  range: string
) => {
  return assemblyType === 'front-layout'
    ? `frontLayout_${width}x${depth}x${height}_${id}`
    : range === 'pax' || range === 'ivar'
    ? `${id}_${width}x${depth}x${height}`
    : `interiorSuggestion${id}_${width}x${depth}x${height}`;
};

const getAssemblyCatalogId = (
  id: string,
  width: number,
  depth: number,
  height: number,
  design: string,
  assemblyType: AssemblyType,
  range: string,
  ageGroup?: string
) => {
  return assemblyType === 'interior-suggestion'
    ? range === 'smastad'
      ? `${getAssemblyTemplateId(id, width, depth, height, assemblyType, range)}_${ageGroup}`
      : design
      ? `${getAssemblyTemplateId(id, width, depth, height, assemblyType, range)}_${design}`
      : `${getAssemblyTemplateId(id, width, depth, height, assemblyType, range)}`
    : getAssemblyTemplateId(id, width, depth, height, assemblyType, range);
};

const getProductType = (range: string, assemblyType: AssemblyType) => {
  if (range === 'pax') {
    return assemblyType === 'interior-suggestion' ? ProductType.PAX_INTERIOR_SUGGESTIONS : '';
  } else if (range === 'besta') {
    return assemblyType === 'interior-suggestion' ? '' : ProductType.BESTA_FRONT_LAYOUT;
  } else if (range === 'platsa') {
    return assemblyType === 'interior-suggestion'
      ? ProductType.PLATSA_INTERIOR_SUGGESTION
      : ProductType.PLATSA_FRONT_LAYOUT;
  } else if (range === 'smastad') {
    return assemblyType === 'interior-suggestion'
      ? ProductType.SMASTAD_INTERIOR_SUGGESTION
      : ProductType.SMASTAD_FRONT_LAYOUT;
  } else if (range === 'ivar') {
    return assemblyType === 'interior-suggestion' ? ProductType.IVAR_INTERIOR_SUGGESTION : '';
  }
  return '';
};

const getImageName = (
  id: string,
  width: number,
  depth: number,
  height: number,
  design: string,
  range: string,
  assemblyType: AssemblyType,
  ageGroup?: string,
  imagePrefix?: string
) => {
  if (!imagePrefix) {
    imagePrefix = '';
  }
  if (assemblyType === 'interior-suggestion') {
    const suffix = range === 'smastad' ? ageGroup : design;
    const suffixString = imagePrefix || suffix ? `_${suffix}${imagePrefix}` : '';
    return range === 'pax' || range === 'ivar'
      ? `images/${range}/interiorSuggestions/${id}_${width}x${depth}x${height}${suffixString}.jpg`
      : `images/${range}/interiorSuggestions/interiorSuggestion${id}_${width}x${depth}x${height}${suffixString}.jpg`;
  } else {
    return range === 'besta'
      ? `images/${range}/frontLayouts/frontLayout_${width}x${depth}x${height}_${id}${imagePrefix}.svg`
      : `images/${range}/frontLayouts/frontLayout_${width}x${height}_${id}${imagePrefix}.svg`;
  }
};

const getAutoAdaptGroup = (id: string, design: string, range: string, ageGroup?: string) => {
  const suffix = range === 'smastad' ? ageGroup : design;
  switch (range) {
    case 'pax':
      return `${id}_${suffix}`;
    case 'ivar':
      return null;
    default:
      return `interiorSuggestion${id}_${suffix}`;
  }
};

const getBaseTemplate = (range: string, assemblyType: AssemblyType) => {
  if (range === 'pax') {
    return assemblyType === 'interior-suggestion' ? 'PAX_Base' : '';
  } else if (range === 'besta') {
    return assemblyType === 'interior-suggestion' ? '' : 'frontLayout_base';
  } else if (range === 'platsa' || range === 'smastad') {
    return assemblyType === 'interior-suggestion' ? 'interiorSuggestion_base' : 'frontLayout_base';
  } else if (range === 'ivar') {
    return assemblyType === 'interior-suggestion' ? 'IS_Base' : '';
  }
  return '';
};

const getMetaData = (
  assemblyId: string,
  width: number,
  depth: number,
  height: number,
  design: string,
  range: string,
  assemblyType: AssemblyType,
  ageGroup?: string,
  applicableFrameTypes?: string[],
  imagePrefix?: string
) => {
  let meta: CatalogMeta = {
    image: getImageName(
      assemblyId,
      width,
      depth,
      height,
      design,
      range,
      assemblyType,
      ageGroup,
      imagePrefix
    )
  };
  if (assemblyType === 'interior-suggestion') {
    const formattedDesign = design.split('-').join(' ');
    if (formattedDesign) {
      meta = { ...meta, design: formattedDesign };
    }
    if (ageGroup && range === 'smastad') {
      meta = { ...meta, ageGroup };
    }
    if (range !== 'pax' && range !== 'ivar') {
      meta = { ...meta, defaultFrontLayout: `frontLayout_${width}x${depth}x${height}_001` };
    }

    if (applicableFrameTypes && applicableFrameTypes?.length > 0) {
      meta = { ...meta, applicableFrameTypes };
    }
  }
  return meta;
};

const getFrontLayoutDexfId = (parts: TemplatePart[]) => {
  const nbrOfDoors = parts.filter(part => part.partKey.includes('door')).length;
  const nbrOfDrawers = parts.filter(part => part.partKey.includes('drawer')).length;
  if (nbrOfDoors === 1 && nbrOfDrawers === 0) return 'ASM-42464271';
  else if (nbrOfDoors > 1 && nbrOfDrawers === 0) return 'ASM-42464334';
  else if (nbrOfDoors === 0 && nbrOfDrawers === 1) return 'ASM-42464335';
  else if (nbrOfDoors === 0 && nbrOfDrawers > 1) return 'ASM-42464336';
  else if (nbrOfDoors > 1 && nbrOfDrawers > 1) return 'ASM-42464337';
  else if (nbrOfDoors === 1 && nbrOfDrawers === 1) return 'ASM-42464338';
  else if (nbrOfDoors > 1 && nbrOfDrawers === 1) return 'ASM-42464339';
  else if (nbrOfDoors === 1 && nbrOfDrawers > 1) return 'ASM-42464340';
  return '';
};

const getDexfId = (range: string, assemblyType: AssemblyType, parts: TemplatePart[]) => {
  if (range === 'pax' || range === 'ivar') {
    return assemblyType === 'interior-suggestion' ? 'ASM-42463486' : '';
  } else if (range === 'besta') {
    return assemblyType === 'interior-suggestion' ? '' : getFrontLayoutDexfId(parts);
  } else if (range === 'platsa' || range === 'smastad') {
    return assemblyType === 'interior-suggestion' ? 'ASM-42463486' : getFrontLayoutDexfId(parts);
  }
  return '';
};

const getChildrenForInteriorSuggestion = (parent: Entity) => {
  return HierarchyUtils.children(parent)
    .filter(child => !child.hasComponent(TopPanelComponent.id))
    .filter(child => !child.hasComponent(ComponentIds.LEG_COMPONENT_ID))
    .filter(child => !child.hasComponent(ComponentIds.DOOR_COMPONENT_ID))
    .filter(child => !child.hasComponent(ComponentIds.PROPPING_COMPONENT_ID))
    .filter(child => !child.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID))
    .filter(child => !child.hasComponent(CompartmentComponent.id))
    .sort(sortByPosition);
};

const getChildrenForFrontLayout = (parent: Entity) => {
  return HierarchyUtils.children(parent)
    .filter(child => !child.hasComponent(TopPanelComponent.id))
    .filter(child => !child.hasComponent(ComponentIds.LEG_COMPONENT_ID))
    .filter(child => !child.hasComponent(CompartmentComponent.id))
    .filter(child => !child.hasComponent(ComponentIds.PROPPING_COMPONENT_ID))
    .filter(child => !child.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID))
    .sort(sortByPosition);
};

const getChildren = (parent: Entity, assemblyType: string) => {
  return assemblyType === 'interior-suggestion'
    ? getChildrenForInteriorSuggestion(parent)
    : getChildrenForFrontLayout(parent);
};

const getEntityData = (entity: Entity, assemblyType: string, index: number = 0): EntityData => {
  const productRef = ProductUtils.productRef(entity);
  if (!productRef) throw new Error(`Couldn't get productRef for entity ${entity}`);
  const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
    ?.productType;
  if (!productType) throw new Error(`Couldn't get productType for entity ${entity}`);
  const transform = TransformUtils.localTransform(entity);
  const rotation = transform.rotation.toEulerDegrees();
  const children = getChildren(entity, assemblyType).map((child, i) =>
    getEntityData(child, assemblyType, index + i + 1)
  );
  return {
    id: productRef,
    partKey: `${productType.replaceAll(' ', '-')}-${index}`,
    position: {
      x: transform.position.X,
      y: transform.position.Y,
      z: transform.position.Z,
      rx: rotation.X,
      ry: rotation.Y,
      rz: rotation.Z
    },
    children
  };
};

const getCatalogParts = (parent: EntityData): TemplatePart[] => {
  return parent.children.flatMap((child: EntityData) => {
    return [{ id: child.id, partKey: child.partKey, main: false }, ...getCatalogParts(child)];
  });
};

const getTemplatePart = (entityData: EntityData): any => {
  const part = {
    partKey: entityData.partKey,
    locked: false,
    connector: '-',
    connectorOnHost: '-',
    position: entityData.position
  };
  return entityData.children.length > 0
    ? { ...part, children: entityData.children.map((child: EntityData) => getTemplatePart(child)) }
    : part;
};

const getTemplateParts = (frame: EntityData): any => {
  return frame.children.map((child: EntityData) => getTemplatePart(child));
};

const getEntitySize = (entity: Entity): EntitySize => {
  const size = entity.getComponent<TemplateDataComponent>(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)
    ?.size;
  const comSize = entity.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  if (!size || !comSize) throw new Error(`Couldn't get size for entity ${entity}`);
  return { size, comSize };
};

const sortByPosition = (entityA: Entity, entityB: Entity) => {
  const positionA = TransformUtils.localPosition(entityA);
  const positionB = TransformUtils.localPosition(entityB);
  return positionB.Y - positionA.Y || positionB.X - positionA.X;
};

export const generateCatalogData = (
  assemblyId: string,
  frame: Entity,
  range: string,
  assemblyType: AssemblyType,
  ageGroup?: string,
  applicableFrameTypes?: string[],
  imagePrefix?: string
): CatalogData => {
  const frameEntity = rangeIsBesta() ? HierarchyUtils.children(frame)[0] : frame;
  const frameData = getEntityData(frameEntity, assemblyType);
  const frameSize = getEntitySize(frame);

  const assemblyDesign = (
    frameEntity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design || ''
  ).replaceAll(' ', '-');
  const id = getAssemblyCatalogId(
    assemblyId,
    frameSize.comSize.width,
    frameSize.comSize.depth,
    frameSize.comSize.height,
    assemblyDesign,
    assemblyType,
    range,
    ageGroup
  );
  const parts = getCatalogParts(frameData);

  let catalogData: CatalogData = {
    id,
    template: {
      id: getAssemblyTemplateId(
        assemblyId,
        frameSize.comSize.width,
        frameSize.comSize.depth,
        frameSize.comSize.height,
        assemblyType,
        range
      ),
      size: { width: frameSize.size.X, depth: frameSize.size.Z, height: frameSize.size.Y },
      comSize: {
        width: frameSize.comSize.width,
        depth: frameSize.comSize.depth,
        height: frameSize.comSize.height
      },
      parts
    },
    type: getProductType(range, assemblyType),
    dexf: getDexfId(range, assemblyType, parts),
    modelURI: '',
    meta: getMetaData(
      assemblyId,
      frameSize.comSize.width,
      frameSize.comSize.depth,
      frameSize.comSize.height,
      assemblyDesign,
      range,
      assemblyType,
      ageGroup,
      applicableFrameTypes,
      imagePrefix
    )
  };
  const autoAdaptGroup = getAutoAdaptGroup(assemblyId, assemblyDesign, range, ageGroup);
  if (assemblyType === 'interior-suggestion' && autoAdaptGroup) {
    catalogData = {
      ...catalogData,
      autoAdaptGroup
    };
  }

  return catalogData;
};

export const generateTemplateData = (
  frame: Entity,
  catalogData: CatalogData,
  range: string,
  assemblyType: AssemblyType
) => {
  const frameEntity = rangeIsBesta() ? HierarchyUtils.children(frame)[0] : frame;
  const frameData = getEntityData(frameEntity, assemblyType);
  const children: any[] = getTemplateParts(frameData);

  return {
    templateId: `${catalogData.template.id}`,
    name: `${catalogData.template.id}`,
    inherits: [getBaseTemplate(range, assemblyType)],
    components: {
      assembly: {
        children
      }
    }
  };
};
